// src/components/learning/islamic/PrayerLearning.js
// Interface interactive pour apprendre la prière

import React, { useState, useEffect } from 'react';
import { 
  wuduSteps, 
  prayerPositions, 
  prayerRecitations, 
  dailyPrayers,
  prayerQuiz,
  prayerEncouragements,
  prayerBadges 
} from './prayerData';
import Confetti from '../Confetti';
import { playArabic } from '../../../utils/audioPlayer';

const PrayerLearning = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState(null); // 'wudu', 'positions', 'recitations', 'practice', 'quiz'
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  
  // Quiz state
  const [quizCategory, setQuizCategory] = useState(null);
  const [quizState, setQuizState] = useState({ currentQuestion: 0, score: 0, showResult: false });
  const [feedback, setFeedback] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);


  // Auto-play pour le mode guidé
  useEffect(() => {
    if (autoPlay && activeSection === 'wudu' && currentStep < wuduSteps.length - 1) {
      const timer = setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, (wuduSteps[currentStep].duration || 5) * 1000);
      return () => clearTimeout(timer);
    }
  }, [autoPlay, currentStep, activeSection]);

  // Quiz handlers
  const getQuizQuestions = () => {
    if (quizCategory === 'all') return prayerQuiz;
    return prayerQuiz.filter(q => q.category === quizCategory);
  };

  const handleQuizAnswer = (answer) => {
    const questions = getQuizQuestions();
    const currentQ = questions[quizState.currentQuestion];
    const isCorrect = answer === currentQ.correct;
    
    if (isCorrect) {
      setFeedback({ type: 'correct', message: prayerEncouragements.correct[Math.floor(Math.random() * prayerEncouragements.correct.length)], explanation: currentQ.explanation });
    } else {
      setFeedback({ type: 'wrong', message: prayerEncouragements.wrong[Math.floor(Math.random() * prayerEncouragements.wrong.length)], explanation: currentQ.explanation });
    }

    setTimeout(() => {
      setFeedback(null);
      const newState = {
        ...quizState,
        score: isCorrect ? quizState.score + 1 : quizState.score,
        currentQuestion: quizState.currentQuestion + 1
      };

      if (newState.currentQuestion >= questions.length) {
        newState.showResult = true;
        if (newState.score >= questions.length * 0.8) {
          setShowConfetti(true);
        }
      }

      setQuizState(newState);
    }, 2000);
  };

  const resetQuiz = () => {
    setQuizState({ currentQuestion: 0, score: 0, showResult: false });
    setShowConfetti(false);
    setQuizCategory(null);
  };

  // ==================== SECTION WUDU ====================
  if (activeSection === 'wudu') {
    const step = wuduSteps[currentStep];
    
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 9999, overflow: 'auto' }}>
        {/* Header */}
        <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => { setActiveSection(null); setCurrentStep(0); setAutoPlay(false); }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}>←</button>
            <span style={{ fontSize: '2rem' }}>💧</span>
            <div>
              <h2 style={{ margin: 0, color: 'white' }}>Les Ablutions (Wudu)</h2>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)' }}>الوُضُوء</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Progress bar */}
        <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)' }}>
          <div style={{ height: '100%', width: `${((currentStep + 1) / wuduSteps.length) * 100}%`, background: 'linear-gradient(90deg, #06b6d4, #10b981)', transition: 'width 0.3s ease' }} />
        </div>

        {/* Contenu */}
        <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
          {/* Numéro et navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <button 
              onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
              disabled={currentStep === 0}
              style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: currentStep === 0 ? 'rgba(255,255,255,0.1)' : '#06b6d4', color: 'white', cursor: currentStep === 0 ? 'default' : 'pointer', opacity: currentStep === 0 ? 0.5 : 1 }}
            >
              ← Précédent
            </button>
            <span style={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold' }}>
              Étape {currentStep + 1} / {wuduSteps.length}
            </span>
            <button 
              onClick={() => setCurrentStep(prev => Math.min(wuduSteps.length - 1, prev + 1))}
              disabled={currentStep === wuduSteps.length - 1}
              style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: currentStep === wuduSteps.length - 1 ? 'rgba(255,255,255,0.1)' : '#06b6d4', color: 'white', cursor: currentStep === wuduSteps.length - 1 ? 'default' : 'pointer', opacity: currentStep === wuduSteps.length - 1 ? 0.5 : 1 }}
            >
              Suivant →
            </button>
          </div>

          {/* Carte de l'étape */}
          <div style={{ background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)', borderRadius: '24px', padding: '2rem', textAlign: 'center', marginBottom: '2rem' }}>
            {step.image ? (
              <img 
                src={step.image} 
                alt={step.name}
                style={{ width: '180px', height: '180px', objectFit: 'contain', marginBottom: '1rem' }}
              />
            ) : (
              <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>{step.icon}</div>
            )}
            
            <h3 style={{ color: 'white', fontSize: '1.5rem', margin: '0 0 0.5rem 0' }}>{step.name}</h3>
            <p 
              onClick={() => playArabic(step.nameAr)}
              style={{ color: '#06b6d4', fontFamily: "'Amiri', serif", fontSize: '1.5rem', margin: '0 0 1.5rem 0', cursor: 'pointer' }}
            >
              {step.nameAr} 🔊
            </p>
            
            <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '1.5rem', marginBottom: '1rem' }}>
              <p style={{ color: 'white', fontSize: '1.2rem', margin: 0, lineHeight: 1.6 }}>
                {step.instruction}
              </p>
              {step.times && (
                <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                  {[...Array(step.times)].map((_, i) => (
                    <span key={i} style={{ fontSize: '1.5rem' }}>💧</span>
                  ))}
                  <span style={{ color: 'rgba(255,255,255,0.7)', marginLeft: '0.5rem' }}>× {step.times}</span>
                </div>
              )}
            </div>
            
            <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0, fontStyle: 'italic' }}>
              {step.details}
            </p>
            
            {step.dua && (
              <div style={{ marginTop: '1.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1rem' }}>
                <p 
                  onClick={() => playArabic(step.duaAr)}
                  style={{ color: '#fbbf24', fontFamily: "'Amiri', serif", fontSize: '1.3rem', margin: '0 0 0.5rem 0', cursor: 'pointer' }}
                >
                  {step.duaAr} 🔊
                </p>
                <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.95rem' }}>
                  {step.dua}
                </p>
                {step.translation && (
                  <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0.5rem 0 0 0', fontSize: '0.85rem', fontStyle: 'italic' }}>
                    "{step.translation}"
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Mini navigation */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {wuduSteps.map((s, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: 'none',
                  background: i === currentStep ? '#06b6d4' : i < currentStep ? '#10b981' : 'rgba(255,255,255,0.1)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1.2rem'
                }}
              >
                {i < currentStep ? '✓' : s.icon}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ==================== SECTION POSITIONS ====================
  if (activeSection === 'positions') {
    const position = prayerPositions[currentStep];
    
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 9999, overflow: 'auto' }}>
        {/* Header */}
        <div style={{ padding: '1.5rem', background: `linear-gradient(135deg, ${position.color} 0%, ${position.color}dd 100%)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => { setActiveSection(null); setCurrentStep(0); }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}>←</button>
            <span style={{ fontSize: '2rem' }}>🙏</span>
            <div>
              <h2 style={{ margin: 0, color: 'white' }}>Les Positions</h2>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)' }}>أَرْكَان الصَّلَاة</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Progress */}
        <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)' }}>
          <div style={{ height: '100%', width: `${((currentStep + 1) / prayerPositions.length) * 100}%`, background: position.color, transition: 'width 0.3s ease' }} />
        </div>

        <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
          {/* Navigation */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <button 
              onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
              disabled={currentStep === 0}
              style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: currentStep === 0 ? 'rgba(255,255,255,0.1)' : position.color, color: 'white', cursor: currentStep === 0 ? 'default' : 'pointer', opacity: currentStep === 0 ? 0.5 : 1 }}
            >
              ←
            </button>
            <span style={{ color: 'white', fontWeight: 'bold' }}>{currentStep + 1} / {prayerPositions.length}</span>
            <button 
              onClick={() => setCurrentStep(prev => Math.min(prayerPositions.length - 1, prev + 1))}
              disabled={currentStep === prayerPositions.length - 1}
              style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: currentStep === prayerPositions.length - 1 ? 'rgba(255,255,255,0.1)' : position.color, color: 'white', cursor: currentStep === prayerPositions.length - 1 ? 'default' : 'pointer', opacity: currentStep === prayerPositions.length - 1 ? 0.5 : 1 }}
            >
              →
            </button>
          </div>

          {/* Carte position */}
          <div style={{ background: `${position.color}20`, borderRadius: '24px', padding: '2rem', textAlign: 'center', marginBottom: '2rem', border: `2px solid ${position.color}` }}>
            {position.image ? (
              <img 
                src={position.image} 
                alt={position.name}
                style={{ width: '180px', height: '180px', objectFit: 'contain', marginBottom: '1rem' }}
              />
            ) : (
              <div style={{ fontSize: '6rem', marginBottom: '1rem' }}>{position.icon}</div>
            )}
            
            <h3 style={{ color: 'white', fontSize: '1.8rem', margin: '0 0 0.5rem 0' }}>{position.name}</h3>
            <p 
              onClick={() => playArabic(position.nameAr)}
              style={{ color: position.color, fontFamily: "'Amiri', serif", fontSize: '1.5rem', margin: '0 0 0.5rem 0', cursor: 'pointer' }}
            >
              {position.nameAr} 🔊
            </p>
            <p style={{ color: 'rgba(255,255,255,0.7)', margin: '0 0 1.5rem 0' }}>({position.meaning})</p>
            
            <p style={{ color: 'white', fontSize: '1.1rem', margin: '0 0 1.5rem 0' }}>
              {position.description}
            </p>
            
            {/* Détails */}
            <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '12px', padding: '1.5rem', textAlign: 'left' }}>
              {position.details.map((detail, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: i < position.details.length - 1 ? '0.75rem' : 0 }}>
                  <span style={{ color: position.color, fontWeight: 'bold' }}>{i + 1}.</span>
                  <span style={{ color: 'rgba(255,255,255,0.9)' }}>{detail}</span>
                </div>
              ))}
            </div>
            
            {/* Dhikr */}
            {position.dhikr && (
              <div style={{ marginTop: '1.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1rem' }}>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>Ce qu'on dit :</p>
                <p 
                  onClick={() => playArabic(position.dhikr)}
                  style={{ color: '#fbbf24', fontFamily: "'Amiri', serif", fontSize: '1.4rem', margin: '0 0 0.5rem 0', cursor: 'pointer' }}
                >
                  {position.dhikr} 🔊
                </p>
                <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0, fontSize: '0.9rem', fontStyle: 'italic' }}>
                  "{position.dhikrTranslation}"
                </p>
              </div>
            )}
            
            {/* 7 parties pour Sujud */}
            {position.sevenParts && (
              <div style={{ marginTop: '1.5rem', background: 'rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1rem' }}>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 0.75rem 0', fontSize: '0.9rem' }}>Les 7 parties qui touchent le sol :</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
                  {position.sevenParts.map((part, i) => (
                    <span key={i} style={{ background: position.color, padding: '0.3rem 0.75rem', borderRadius: '20px', color: 'white', fontSize: '0.85rem' }}>
                      {part}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mini navigation */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
            {prayerPositions.map((p, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                style={{
                  width: '45px',
                  height: '45px',
                  borderRadius: '50%',
                  border: i === currentStep ? `3px solid ${p.color}` : 'none',
                  background: i === currentStep ? `${p.color}40` : 'rgba(255,255,255,0.1)',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '1.3rem'
                }}
              >
                {p.icon}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ==================== SECTION RÉCITATIONS ====================
  if (activeSection === 'recitations') {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 9999, overflow: 'auto' }}>
        {/* Header */}
        <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => setActiveSection(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}>←</button>
            <span style={{ fontSize: '2rem' }}>📖</span>
            <div>
              <h2 style={{ margin: 0, color: 'white' }}>Les Paroles</h2>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)' }}>أَذْكَار الصَّلَاة</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
          {prayerRecitations.map((recitation, index) => (
            <div 
              key={index}
              style={{
                background: `${recitation.color}15`,
                borderRadius: '16px',
                padding: '1.5rem',
                marginBottom: '1rem',
                borderLeft: `4px solid ${recitation.color}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>{recitation.icon}</span>
                  <div>
                    <h4 style={{ margin: 0, color: 'white' }}>{recitation.name}</h4>
                    <p style={{ margin: 0, color: recitation.color, fontFamily: "'Amiri', serif" }}>{recitation.nameAr}</p>
                  </div>
                </div>
                {recitation.times && (
                  <span style={{ background: recitation.color, padding: '0.25rem 0.75rem', borderRadius: '20px', color: 'white', fontSize: '0.8rem' }}>
                    ×{recitation.times}
                  </span>
                )}
              </div>
              
              <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
                📍 {recitation.when}
              </p>
              
              <div 
                onClick={() => playArabic(recitation.arabic)}
                style={{ 
                  background: 'rgba(0,0,0,0.3)', 
                  borderRadius: '12px', 
                  padding: '1rem', 
                  marginBottom: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                <p style={{ color: 'white', fontFamily: "'Amiri', serif", fontSize: '1.4rem', margin: 0, textAlign: 'right', direction: 'rtl', lineHeight: 1.8 }}>
                  {recitation.arabic} 🔊
                </p>
              </div>
              
              <p style={{ color: recitation.color, margin: '0 0 0.5rem 0', fontStyle: 'italic' }}>
                {recitation.transliteration}
              </p>
              
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.95rem' }}>
                "{recitation.translation}"
              </p>
              
              {recitation.response && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                  <p style={{ color: 'rgba(255,255,255,0.6)', margin: '0 0 0.5rem 0', fontSize: '0.85rem' }}>Puis on dit :</p>
                  <p 
                    onClick={() => playArabic(recitation.response)}
                    style={{ color: '#fbbf24', fontFamily: "'Amiri', serif", fontSize: '1.2rem', margin: '0 0 0.25rem 0', cursor: 'pointer' }}
                  >
                    {recitation.response} 🔊
                  </p>
                  <p style={{ color: 'rgba(255,255,255,0.7)', margin: 0, fontSize: '0.85rem', fontStyle: 'italic' }}>
                    "{recitation.responseTranslation}"
                  </p>
                </div>
              )}
              
              {recitation.note && (
                <p style={{ color: '#fbbf24', margin: '0.75rem 0 0 0', fontSize: '0.85rem' }}>
                  💡 {recitation.note}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ==================== SECTION QUIZ ====================
  if (activeSection === 'quiz') {
    // Choix de catégorie
    if (!quizCategory) {
      return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 9999, overflow: 'auto' }}>
          <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button onClick={() => setActiveSection(null)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}>←</button>
              <span style={{ fontSize: '2rem' }}>📝</span>
              <h2 style={{ margin: 0, color: 'white' }}>Quiz</h2>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer' }}>✕</button>
          </div>

          <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto' }}>
            <p style={{ color: 'white', textAlign: 'center', marginBottom: '2rem', fontSize: '1.1rem' }}>
              Choisis un thème pour tester tes connaissances !
            </p>
            
            {[
              { id: 'wudu', name: 'Les Ablutions', icon: '💧', color: '#06b6d4', questions: prayerQuiz.filter(q => q.category === 'wudu').length },
              { id: 'positions', name: 'Les Positions', icon: '🙏', color: '#10b981', questions: prayerQuiz.filter(q => q.category === 'positions').length },
              { id: 'recitations', name: 'Les Paroles', icon: '📖', color: '#8b5cf6', questions: prayerQuiz.filter(q => q.category === 'recitations').length },
              { id: 'daily', name: 'Les 5 Prières', icon: '🕌', color: '#f59e0b', questions: prayerQuiz.filter(q => q.category === 'daily').length },
              { id: 'all', name: 'Quiz Complet', icon: '🏆', color: '#ef4444', questions: prayerQuiz.length }
            ].map(cat => (
              <div
                key={cat.id}
                onClick={() => setQuizCategory(cat.id)}
                style={{
                  background: `${cat.color}20`,
                  borderRadius: '16px',
                  padding: '1.5rem',
                  marginBottom: '1rem',
                  cursor: 'pointer',
                  border: `2px solid transparent`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = cat.color; e.currentTarget.style.transform = 'translateX(5px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}
              >
                <div style={{ fontSize: '2.5rem' }}>{cat.icon}</div>
                <div style={{ flex: 1 }}>
                  <h4 style={{ margin: 0, color: 'white' }}>{cat.name}</h4>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>{cat.questions} questions</p>
                </div>
                <span style={{ color: cat.color, fontSize: '1.5rem' }}>→</span>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Résultat
    if (quizState.showResult) {
      const questions = getQuizQuestions();
      const percentage = Math.round((quizState.score / questions.length) * 100);
      const passed = percentage >= 80;
      
      return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          {showConfetti && <Confetti type="gold" duration={4000} />}
          
          <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderRadius: '24px', padding: '3rem', textAlign: 'center', maxWidth: '500px' }}>
            <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>{passed ? '🏆' : '⭐'}</div>
            <h2 style={{ color: 'white', margin: '0 0 1rem 0' }}>{passed ? 'Félicitations !' : 'Continue d\'apprendre !'}</h2>
            
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1.5rem', marginBottom: '2rem' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: passed ? '#10b981' : '#f59e0b' }}>
                {quizState.score}/{questions.length}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.7)' }}>{percentage}%</div>
            </div>
            
            {passed && quizCategory !== 'all' && (
              <div style={{ background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', borderRadius: '12px', padding: '1rem', marginBottom: '2rem' }}>
                <span style={{ fontSize: '2rem' }}>{prayerBadges[quizCategory]?.icon}</span>
                <div style={{ color: '#1f2937', fontWeight: 'bold' }}>Badge "{prayerBadges[quizCategory]?.name}" débloqué !</div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button onClick={resetQuiz} style={{ padding: '1rem 2rem', borderRadius: '12px', border: 'none', background: '#f59e0b', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
                🔄 Rejouer
              </button>
              <button onClick={() => { resetQuiz(); setActiveSection(null); }} style={{ padding: '1rem 2rem', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
                ← Retour
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Question en cours
    const questions = getQuizQuestions();
    const currentQ = questions[quizState.currentQuestion];
    
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', display: 'flex', flexDirection: 'column', zIndex: 9999 }}>
        <div style={{ padding: '1rem 1.5rem', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={resetQuiz} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: 'white', cursor: 'pointer' }}>←</button>
            <span style={{ color: 'white', fontWeight: 'bold' }}>Question {quizState.currentQuestion + 1}/{questions.length}</span>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.5rem 1rem', borderRadius: '8px', color: 'white' }}>⭐ {quizState.score}</div>
        </div>

        <div style={{ height: '4px', background: 'rgba(255,255,255,0.1)' }}>
          <div style={{ height: '100%', width: `${((quizState.currentQuestion) / questions.length) * 100}%`, background: '#f59e0b', transition: 'width 0.3s ease' }} />
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: '2rem' }}>
          <h3 style={{ color: 'white', fontSize: '1.5rem', textAlign: 'center', maxWidth: '600px' }}>{currentQ.question}</h3>

          {feedback && (
            <div style={{ padding: '1rem 2rem', borderRadius: '12px', background: feedback.type === 'correct' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)', color: feedback.type === 'correct' ? '#10b981' : '#ef4444', textAlign: 'center', maxWidth: '500px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>{feedback.message}</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>{feedback.explanation}</div>
            </div>
          )}

          {!feedback && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', maxWidth: '500px', width: '100%' }}>
              {currentQ.options.map((option, i) => (
                <button
                  key={i}
                  onClick={() => handleQuizAnswer(option)}
                  style={{ padding: '1.5rem', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: 'white', fontSize: '1.1rem', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={(e) => { e.target.style.borderColor = '#f59e0b'; e.target.style.transform = 'scale(1.02)'; }}
                  onMouseLeave={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.2)'; e.target.style.transform = 'scale(1)'; }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== VUE PRINCIPALE ====================
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 9999, overflow: 'auto' }}>
      {/* Header */}
      <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderBottom: '2px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '2.5rem' }}>🤲</span>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.5rem' }}>Apprendre la Prière</h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)' }}>تَعَلُّم الصَّلَاة</p>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
      </div>

      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
        {/* Introduction */}
        <div style={{ background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)', borderRadius: '16px', padding: '1.5rem', marginBottom: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'white', fontSize: '1.1rem', margin: 0 }}>
            La prière (Salat) est le 2ème pilier de l'Islam. 
            Apprends étape par étape ! 🌟
          </p>
        </div>

        {/* Les 4 sections */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
          {/* Wudu */}
          <div
            onClick={() => setActiveSection('wudu')}
            style={{
              background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(6, 182, 212, 0.1) 100%)',
              borderRadius: '20px',
              padding: '2rem',
              cursor: 'pointer',
              border: '2px solid transparent',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#06b6d4'; e.currentTarget.style.transform = 'translateY(-5px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>💧</div>
            <h3 style={{ color: 'white', margin: '0 0 0.5rem 0' }}>Les Ablutions</h3>
            <p style={{ color: '#06b6d4', fontFamily: "'Amiri', serif", margin: '0 0 0.5rem 0' }}>الوُضُوء</p>
            <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.9rem' }}>11 étapes</p>
          </div>

          {/* Positions */}
          <div
            onClick={() => setActiveSection('positions')}
            style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)',
              borderRadius: '20px',
              padding: '2rem',
              cursor: 'pointer',
              border: '2px solid transparent',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#10b981'; e.currentTarget.style.transform = 'translateY(-5px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🙏</div>
            <h3 style={{ color: 'white', margin: '0 0 0.5rem 0' }}>Les Positions</h3>
            <p style={{ color: '#10b981', fontFamily: "'Amiri', serif", margin: '0 0 0.5rem 0' }}>أَرْكَان الصَّلَاة</p>
            <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.9rem' }}>8 positions</p>
          </div>

          {/* Récitations */}
          <div
            onClick={() => setActiveSection('recitations')}
            style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)',
              borderRadius: '20px',
              padding: '2rem',
              cursor: 'pointer',
              border: '2px solid transparent',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#8b5cf6'; e.currentTarget.style.transform = 'translateY(-5px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📖</div>
            <h3 style={{ color: 'white', margin: '0 0 0.5rem 0' }}>Les Paroles</h3>
            <p style={{ color: '#8b5cf6', fontFamily: "'Amiri', serif", margin: '0 0 0.5rem 0' }}>أَذْكَار الصَّلَاة</p>
            <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.9rem' }}>12 récitations</p>
          </div>

          {/* Quiz */}
          <div
            onClick={() => setActiveSection('quiz')}
            style={{
              background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(245, 158, 11, 0.1) 100%)',
              borderRadius: '20px',
              padding: '2rem',
              cursor: 'pointer',
              border: '2px solid transparent',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#f59e0b'; e.currentTarget.style.transform = 'translateY(-5px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📝</div>
            <h3 style={{ color: 'white', margin: '0 0 0.5rem 0' }}>Quiz</h3>
            <p style={{ color: '#f59e0b', fontFamily: "'Amiri', serif", margin: '0 0 0.5rem 0' }}>اِخْتِبَار</p>
            <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.9rem' }}>8 questions</p>
          </div>
        </div>

        {/* Les 5 prières */}
        <h3 style={{ color: 'white', marginBottom: '1rem' }}>🕌 Les 5 Prières Quotidiennes</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', marginBottom: '2rem' }}>
          {dailyPrayers.map(prayer => (
            <div key={prayer.id} style={{ background: `${prayer.color}20`, borderRadius: '12px', padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{prayer.icon}</div>
              <div style={{ color: 'white', fontWeight: 'bold', fontSize: '0.9rem' }}>{prayer.name}</div>
              <div style={{ color: prayer.color, fontFamily: "'Amiri', serif", fontSize: '0.85rem' }}>{prayer.nameAr}</div>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginTop: '0.25rem' }}>{prayer.rakaat} unités</div>
            </div>
          ))}
        </div>

        {/* Badges */}
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '1.5rem' }}>
          <h4 style={{ color: 'white', marginTop: 0, marginBottom: '1rem' }}>🏆 Badges à débloquer</h4>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            {Object.entries(prayerBadges).map(([key, badge]) => (
              <div key={key} style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1rem', textAlign: 'center', minWidth: '120px' }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>{badge.icon}</div>
                <div style={{ color: 'white', fontSize: '0.85rem', fontWeight: 'bold' }}>{badge.name}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrayerLearning;
